import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Tag, ArrowRight } from 'lucide-react';

const newsArticles = [
  {
    id: 'new-distribution-partnership',
    title: 'Rushmore Publishing Announces New Book Distribution Partnership',
    date: 'Jan 18, 2025',
    category: 'Company News',
    excerpt: 'We are excited to announce our new partnership with major distributors to expand global reach for our authors.',
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
  },
  {
    id: 'award-winning-author-joins',
    title: 'Award-Winning Author Joins Rushmore Publishing Team',
    date: 'Jan 16, 2025',
    category: 'Team Update',
    excerpt: 'Bestselling author and Pulitzer Prize nominee joins our editorial team to mentor emerging writers.',
    image: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
  },
  {
    id: 'expanded-global-distribution',
    title: 'Rushmore Publishing Expands Global Distribution Network',
    date: 'Jan 14, 2025',
    category: 'Expansion',
    excerpt: 'New partnerships enable distribution to 50+ countries, opening opportunities for authors worldwide.',
    image: 'https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
  },
  {
    id: 'digital-publishing-platform',
    title: 'Launch of New Digital Publishing Platform',
    date: 'Jan 10, 2025',
    category: 'Technology',
    excerpt: 'Revolutionary new platform streamlines the publishing process with AI-powered tools for authors.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
  },
  {
    id: 'author-summit-2025',
    title: 'Rushmore Publishing to Host Annual Author Summit 2025',
    date: 'Jan 5, 2025',
    category: 'Events',
    excerpt: 'Three-day virtual summit featuring industry experts, bestselling authors, and networking opportunities.',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
  },
  {
    id: 'record-breaking-year',
    title: 'Rushmore Publishing Reports Record-Breaking Year',
    date: 'Dec 28, 2024',
    category: 'Company News',
    excerpt: 'Over 500 books published in 2024, with 50+ bestsellers and numerous literary awards.',
    image: 'https://images.unsplash.com/photo-1533227268428-f9ed0900fb3b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'
  }
];

const categories = ['All', 'Company News', 'Team Update', 'Expansion', 'Technology', 'Events'];

const NewsList = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredNews = selectedCategory === 'All'
    ? newsArticles
    : newsArticles.filter(article => article.category === selectedCategory);

  return (
    <>
      <Helmet>
        <title>News & Updates | Rushmore Publishing</title>
        <meta name="description" content="Stay updated with the latest news, announcements, and updates from Rushmore Publishing." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white pt-24 pb-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              News & <span className="text-amber-700">Updates</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stay informed about our latest partnerships, achievements, and industry insights
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-amber-700 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* News Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.map(article => (
              <article key={article.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <Link to={`/news/${article.id}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-amber-700 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <Calendar className="w-4 h-4" />
                      {article.date}
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-3 hover:text-amber-700 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-gray-600 mb-4">
                      {article.excerpt}
                    </p>
                    <span className="text-amber-700 font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                      Read More <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default NewsList;